import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Key, Copy, Trash, Plus } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";

const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never";

export const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = () => api.apiKeys().then(d => setKeys(d.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the key a name first");
    setBusy(true);
    try {
      const r = await api.createApiKey({ name: name.trim() });
      setNewKey(r);
      setName("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try {
      await api.revokeApiKey(k.id);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="api-keys-panel">
      <div className="flex items-center gap-2">
        <Key size={16} className="text-zinc-500" />
        <h3 className="font-heading text-base font-medium">API keys for integrations</h3>
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        Read-only access to your contacts for external tools (AI agents, scripts). Keys can search and read contact details — they can never edit, delete or send. Engagement data is not exposed.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Key name, e.g. Wingman agent"
          data-testid="api-key-name" className="ch-input max-w-xs" />
        <button onClick={create} disabled={busy} data-testid="api-key-create"
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
          <Plus size={14} /> Generate key
        </button>
      </div>

      {newKey && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="api-key-reveal">
          <p className="text-xs font-medium text-amber-800">Copy this key now — it won't be shown again.</p>
          <div className="mt-1.5 flex items-center gap-2">
            <code className="text-[12px] font-mono bg-white border border-amber-200 rounded px-2 py-1 break-all select-all" data-testid="api-key-value">{newKey.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Copied"); }}
              data-testid="api-key-copy" className="shrink-0 h-7 px-2 rounded-md border border-amber-300 text-xs text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
            <button onClick={() => setNewKey(null)} className="shrink-0 text-xs text-amber-700 underline">Done</button>
          </div>
          <p className="text-[11px] text-amber-700 mt-2">
            Use it with: <code className="font-mono">GET /api/ext/v1/contacts?q=...</code> and header <code className="font-mono">X-API-Key: {"<key>"}</code>
          </p>
        </div>
      )}

      {keys.length > 0 && (
        <table className="w-full text-sm mt-4">
          <thead className="text-xs text-zinc-500 border-b border-zinc-200">
            <tr>
              <th className="text-left py-1.5 font-medium">Name</th>
              <th className="text-left py-1.5 font-medium">Key</th>
              <th className="text-left py-1.5 font-medium">Created</th>
              <th className="text-left py-1.5 font-medium">Last used</th>
              <th className="text-right py-1.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} className="border-b border-zinc-100 last:border-0" data-testid={`api-key-row-${k.name}`}>
                <td className="py-2 font-medium text-zinc-900">{k.name}</td>
                <td className="py-2 font-mono text-[12px] text-zinc-500">{k.prefix}</td>
                <td className="py-2 text-xs text-zinc-500">{fmtWhen(k.created_at)}</td>
                <td className="py-2 text-xs text-zinc-500">{fmtWhen(k.last_used_at)}</td>
                <td className="py-2 text-right">
                  <button onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.name}`}
                    className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"><Trash size={12} /> Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default ApiKeysPanel;
